import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['hsl(142, 76%, 36%)', 'hsl(25, 95%, 53%)', 'hsl(197, 71%, 73%)', 'hsl(45, 93%, 47%)', 'hsl(280, 65%, 60%)', 'hsl(340, 75%, 55%)', 'hsl(200, 70%, 50%)'];

// Demo data with phases structure
const DEMO_DATA = {
  monthly_budget: 2000,
  phases: [
    {
      phase_number: 1,
      name: "Emergency Fund Building",
      month_start: 1,
      month_end: 4,
      allocations: [
        { product: "High-Yield Savings Account", ticker: "HYSA", percentage: 100, monthly_amount: 2000, rationale: "Building 6-month emergency fund - top priority for financial security" }
      ]
    },
    {
      phase_number: 2,
      name: "Debt Payoff & Initial Investing",
      month_start: 5,
      month_end: 12,
      allocations: [
        { product: "Credit Card Debt Repayment", ticker: "N/A", percentage: 50, monthly_amount: 1000, rationale: "Aggressive payoff of high-interest debt" },
        { product: "Roth IRA - Total Stock Market", ticker: "VTI", percentage: 30, monthly_amount: 600, rationale: "Tax-advantaged long-term growth" },
        { product: "High-Yield Savings", ticker: "HYSA", percentage: 20, monthly_amount: 400, rationale: "Continue building emergency reserves" }
      ]
    },
    {
      phase_number: 3,
      name: "Long-Term Wealth Building",
      month_start: 13,
      month_end: null,
      allocations: [
        { product: "401(k) with Employer Match", ticker: "401K", percentage: 40, monthly_amount: 800, rationale: "Maximize employer match - free money" },
        { product: "Roth IRA - Total Stock Market", ticker: "VTI", percentage: 35, monthly_amount: 700, rationale: "Tax-free growth for retirement" },
        { product: "Taxable Brokerage - International", ticker: "VXUS", percentage: 15, monthly_amount: 300, rationale: "Global diversification" },
        { product: "High-Yield Savings (Goals)", ticker: "HYSA", percentage: 10, monthly_amount: 200, rationale: "Short-term goals fund" }
      ]
    }
  ],
  reasoning: "This plan prioritizes building a solid financial foundation. Phase 1 focuses on emergency savings for security. Phase 2 tackles high-interest debt while starting tax-advantaged investing. Phase 3 maximizes long-term wealth building through diversified investments.",
  calculations: {
    "Phase 1 Duration": "4 months to build $8,000 emergency fund",
    "Phase 2 Duration": "8 months to pay off $8,000 credit card debt",
    "Phase 3": "Ongoing wealth accumulation"
  }
};

export default function DemoDashboard() {
  const navigate = useNavigate();
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(0);

  const phases = DEMO_DATA.phases;
  const monthlyBudget = DEMO_DATA.monthly_budget;
  const currentNetworth = 0; // Demo user starts from scratch

  // Get current phase and its allocations
  const currentPhase = phases[selectedPhaseIndex] || phases[0];
  const currentAllocations = (currentPhase?.allocations || []).map(item => ({
    product: item.product,
    name: item.ticker || item.product?.split(' ')[0] || 'Unknown',
    ticker: item.ticker,
    value: item.percentage || 0,
    monthly_amount: item.monthly_amount || 0,
    rationale: item.rationale || ''
  }));

  const monthlyTotal = currentAllocations.reduce((sum, item) => sum + item.monthly_amount, 0);

  // Format phase label for tabs
  const formatPhaseLabel = (phase) => {
    const start = phase.month_start || 1;
    const end = phase.month_end;
    if (end === null || end === undefined) {
      return `M${start}+`;
    }
    return `M${start}-${end}`;
  };

  // Calculate growth projections
  const avgGrowthRate = 0.075;
  const calculateFutureValue = (years) => {
    if (years === 0) return currentNetworth;
    const currentGrowth = currentNetworth * Math.pow(1 + avgGrowthRate, years);
    const monthlyRate = avgGrowthRate / 12;
    const months = years * 12;
    const contributionsGrowth = monthlyBudget * (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate * (1 + monthlyRate);
    return Math.round(currentGrowth + contributionsGrowth);
  };

  const annualContribution = monthlyBudget * 12;
  const growthChartData = [];
  for (let year = 0; year <= 10; year++) {
    growthChartData.push({
      year: year,
      invested: currentNetworth + (annualContribution * year),
      projected: calculateFutureValue(year)
    });
  }

  const fiveYearTarget = calculateFutureValue(5);
  const tenYearTarget = calculateFutureValue(10);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Orbit" className="w-14 h-14" />
            <span className="text-4xl font-bold" style={{ fontFamily: "'Apple Garamond', serif" }}>Orbit</span>
          </div>
          <Button onClick={() => navigate('/login')}>Create My Plan</Button>
        </div>
      </header>

      {/* Demo Banner */}
      <div className="bg-yellow-500/10 border-b border-yellow-500/20 py-3">
        <div className="flex items-center justify-center gap-2 text-sm font-medium">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <span>This is a demo with sample data. Sign up to create your personalized plan.</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Current Net Worth</p>
            <p className="text-2xl font-bold font-mono">${currentNetworth.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Monthly Budget</p>
            <p className="text-2xl font-bold font-mono">${monthlyBudget.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">5-Year Target</p>
            <p className="text-2xl font-bold font-mono">${fiveYearTarget.toLocaleString()}</p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">10-Year Target</p>
            <p className="text-2xl font-bold font-mono">${tenYearTarget.toLocaleString()}</p>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">Allocation by Phase</h3>
              <p className="text-sm text-muted-foreground mb-4">Select a phase to view its allocation</p>
              <div className="flex flex-wrap gap-2">
                {phases.map((phase, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPhaseIndex(idx)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedPhaseIndex === idx
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80 text-foreground'
                    }`}
                  >
                    {phase.name || `Phase ${phase.phase_number}`}
                    <span className={`ml-1 ${selectedPhaseIndex === idx ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      ({formatPhaseLabel(phase)})
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={currentAllocations}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name} ${value}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {currentAllocations.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}% ($${props.payload.monthly_amount.toLocaleString()})`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">{currentPhase?.name || 'Phase'} Strategy:</p>
              {currentAllocations.map((item, idx) => (
                <p key={idx} className="text-xs text-muted-foreground mb-1">
                  • {item.name}: {item.rationale}
                </p>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">Expected Growth (10 Years)</h3>
              <p className="text-sm text-muted-foreground">Based on ~7.5% average annual return</p>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growthChartData}>
                  <defs>
                    <linearGradient id="investedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(197, 71%, 73%)" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="hsl(197, 71%, 73%)" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Years', position: 'insideBottom', offset: -5 }}
                    ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    domain={[0, 'auto']}
                  />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="invested" 
                    stroke="hsl(197, 71%, 73%)" 
                    strokeWidth={3} 
                    fill="url(#investedGradient)"
                    name="Total Invested"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="projected" 
                    stroke="hsl(142, 76%, 36%)" 
                    strokeWidth={4} 
                    fill="url(#projectedGradient)"
                    name="Projected Value"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Phase Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{currentPhase?.name || 'Investment Plan'}</h3>
              <span className="text-xl font-bold font-mono text-primary">${monthlyTotal.toLocaleString()}/mo</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {currentPhase?.month_end 
                ? `Month ${currentPhase.month_start} - ${currentPhase.month_end}`
                : `Month ${currentPhase?.month_start || 1}+`
              }
            </p>
            <div className="space-y-4">
              {currentAllocations.map((item, idx) => (
                <div key={idx} className="pb-4 border-b last:border-0">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">{item.product}</span>
                    <span className="font-mono font-semibold">${item.monthly_amount.toLocaleString()}</span>
                  </div>
                  {item.ticker && <p className="text-xs font-mono text-primary mb-1">Ticker: {item.ticker}</p>}
                  <p className="text-sm text-muted-foreground">{item.rationale}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4">Overall Strategy</h3>
            <p className="text-muted-foreground leading-relaxed mb-6">{DEMO_DATA.reasoning}</p>
            
            <h4 className="text-lg font-semibold mb-3">Phase Calculations</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              {Object.entries(DEMO_DATA.calculations).map(([key, value]) => (
                <p key={key}>
                  <strong>{key.replace(/_/g, ' ')}:</strong>{' '}
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </p>
              ))}
            </div>
          </Card>
        </div>

        {/* All Phases Overview */}
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-bold mb-4">All Phases Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {phases.map((phase, idx) => (
              <div 
                key={idx} 
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedPhaseIndex === idx ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedPhaseIndex(idx)}
              >
                <h4 className="font-semibold mb-1">{phase.name || `Phase ${phase.phase_number}`}</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  {phase.month_end 
                    ? `Month ${phase.month_start} - ${phase.month_end}`
                    : `Month ${phase.month_start}+`
                  }
                </p>
                <div className="space-y-1">
                  {phase.allocations?.slice(0, 3).map((alloc, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span>{alloc.ticker || alloc.product?.split(' ')[0]}</span>
                      <span className="font-mono">{alloc.percentage}%</span>
                    </div>
                  ))}
                  {(phase.allocations?.length || 0) > 3 && (
                    <p className="text-xs text-muted-foreground">+{phase.allocations.length - 3} more...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* CTA */}
        <Card className="p-8 text-center bg-primary/5 border-primary/20">
          <h3 className="text-2xl font-bold mb-2">Ready to create your personalized plan?</h3>
          <p className="text-muted-foreground mb-6">Answer a few questions and get AI-powered investment recommendations tailored to your goals.</p>
          <Button size="lg" onClick={() => navigate('/login')}>
            Get Started Free
          </Button>
        </Card>
      </div>
    </div>
  );
}
